import { wgs84ToGcj02 } from "./coordinate.js";
import { isCityStyleShop } from "./quality.js";

export const TEA_SCENARIOS = Object.freeze([
  { id: "all", label: "全部茶事" },
  { id: "view", label: "看风景" },
  { id: "yard", label: "找院子" },
  { id: "old", label: "老茶馆" },
  { id: "mountain", label: "上山喝茶" }
]);

export const TEA_TYPES = Object.freeze([
  { id: "all", label: "全部玩法" },
  { id: "tea", label: "喝茶" },
  { id: "farm", label: "农家乐" },
  { id: "camp", label: "露营野炊" },
  { id: "view", label: "看风景" },
  { id: "family", label: "亲子采摘" },
  { id: "stay", label: "民宿过夜" },
  { id: "show", label: "演出活动" },
  { id: "food", label: "聚餐吃饭" }
]);

export const TEA_SORTS = Object.freeze([
  { id: "popular", label: "人气优先" },
  { id: "rating", label: "高分优先" },
  { id: "nearby", label: "离我最近" }
]);

const TYPE_PATTERNS = {
  tea: /茶|茗|盏|盖碗|围炉|茶艺|茶舍|茶室|茶院|茶铺|茶空间|茶馆/,
  farm: /农家乐|农家菜|柴火鸡|土菜|山庄|院坝|农场/,
  camp: /露营|野炊|营地|日归|烧烤/,
  view: /观景|看景|日落|日出|湖景|山景|露台|江边|河畔|公园|自然景观|森林公园|景观|赏景/,
  family: /亲子|采摘|农场|果园|动物|研学|宝宝|儿童/,
  stay: /民宿|过夜|住宿|酒店|山居/,
  show: /演出|川剧|变脸|剧场|剧院|live|驻唱|活动中心|音乐/,
  food: /川菜|火锅|烧烤|柴火鸡|特色菜|私房菜|美食|餐厅|烤全羊|烤羊|鱼庄|土菜|中餐/
};

function textOf(value) {
  return Array.isArray(value) ? value.join(" ") : String(value ?? "");
}

function searchableText(value) {
  return [
    value.category,
    value.teaStyle,
    value.mood,
    value.duration,
    value.name,
    ...(value.tags ?? []),
    ...(value.bestFor ?? []),
    ...(value.details ?? []).map((detail) => detail.value)
  ]
    .map(textOf)
    .join(" ");
}

function scenarioIds(value) {
  const searchable = searchableText(value);
  const scenarios = [];
  if (/观景|看景|日落|日出|湖景|山景|露台|山腰茶咖/.test(searchable)) {
    scenarios.push("view");
  }
  if (/院子|围炉|庭院|茶艺|茶席|陶艺|亲子|宠物/.test(searchable)) {
    scenarios.push("yard");
  }
  if (/老茶馆|老茶铺|盖碗|茶馆老铺|市井|高性价比/.test(searchable)) {
    scenarios.push("old");
  }
  if (/龙泉山|山泉镇|山腰|山岳|丹景台|寺庙|禅茶|上山/.test(searchable)) {
    scenarios.push("mountain");
  }
  return scenarios;
}

export function typeIds(value) {
  const searchable = searchableText(value);
  return Object.entries(TYPE_PATTERNS)
    .filter(([, pattern]) => pattern.test(searchable))
    .map(([id]) => id);
}

function normalizeMappablePlace(feature, index) {
  const properties = feature.properties ?? {};
  const [lng, lat] = feature.geometry.coordinates;
  const [displayLng, displayLat] = wgs84ToGcj02(lng, lat);
  const place = {
    id: properties.id,
    mapId: index + 1,
    name: properties.name,
    area: properties.area,
    category: properties.category,
    priority: properties.priority,
    duration: properties.duration,
    teaStyle: properties.teaStyle,
    description: properties.description,
    tip: properties.tip,
    details: properties.details ?? [],
    coordinatePrecision: properties.coordinatePrecision,
    coordinateSource: properties.coordinateSource,
    dataTier: properties.dataTier ?? "curated",
    qualityTier: "indexed",
    priceNote: properties.priceNote,
    ratingNote: properties.ratingNote,
    tags: properties.tags ?? [],
    source: properties.source,
    osmUrl: properties.osmUrl,
    xhsUrl: properties.xhsUrl,
    featured: false,
    dataTier: "editorial",
    mappable: true,
    lng,
    lat,
    displayLng,
    displayLat,
    cityStyle: isCityStyleShop({
      name: properties.name,
      category: properties.category
    }),
    searchText: `${properties.name} ${properties.area} ${properties.category} ${properties.teaStyle}`.toLocaleLowerCase(
      "zh-CN"
    )
  };
  return {
    ...place,
    scenarios: scenarioIds(place),
    types: typeIds(place)
  };
}

function normalizeEditorialPlace(shop) {
  const place = {
    id: shop.id,
    mapId: null,
    name: shop.name,
    area: shop.area,
    category: shop.category,
    priority: shop.priority,
    duration: shop.duration,
    teaStyle: shop.teaStyle,
    description: shop.description,
    tip: shop.tip,
    details: shop.details ?? [],
    coordinatePrecision: shop.coordinatePrecision,
    coordinateSource: shop.coordinateSource,
    price: shop.price,
    rating: shop.rating,
    reviewCount: shop.reviewCount,
    address: shop.address,
    hours: shop.hours,
    tags: shop.tags ?? [],
    reviewThemes: shop.reviewThemes ?? [],
    image: shop.image,
    source: shop.source,
    featured: shop.featured === true,
    dataTier: shop.dataTier ?? "editorial",
    qualityTier: shop.qualityTier ?? "indexed",
    reviewSnapshot: shop.reviewSnapshot === true,
    videoClues: shop.videoClues ?? [],
    heat: (shop.videoClues ?? []).reduce(
      (peak, clue) => Math.max(peak, Number(clue.stats?.likes) || 0),
      0
    ),
    mappable: false,
    cityStyle: isCityStyleShop(shop),
    searchText: `${shop.name} ${shop.area} ${shop.category} ${shop.tags?.join(" ")}`.toLocaleLowerCase(
      "zh-CN"
    )
  };
  return {
    ...place,
    scenarios: scenarioIds(place),
    types: typeIds(place)
  };
}

function normalizedName(value) {
  return String(value ?? "")
    .normalize("NFKC")
    .replaceAll(/\s+/g, "")
    .toLocaleLowerCase("zh-CN");
}

function normalizeDiscoveryPlace(discovery) {
  const stats = discovery.stats ?? {};
  const tags = [...(discovery.highlights ?? []), ...(discovery.commentThemes ?? [])];
  const place = {
    id: discovery.id,
    mapId: null,
    name: discovery.name,
    area: discovery.area ?? "成都",
    category: "抖音线索",
    priority: "抖音发现",
    duration: "半日",
    teaStyle: (discovery.highlights ?? []).slice(0, 2).join(" · ") || "视频现场",
    description: discovery.summary ?? discovery.commentSummary ?? "",
    tip: discovery.caveat ?? "",
    details: [
      {
        label: "视频数据",
        value: `${stats.likes ?? 0} 赞 · ${stats.comments ?? 0} 评论 · ${stats.collects ?? 0} 收藏`
      },
      { label: "位置线索", value: discovery.location ?? "视频未给出具体门牌" }
    ],
    coordinatePrecision: "抖音公开视频未提供可核实坐标",
    coordinateSource: "抖音公开视频",
    price: null,
    rating: null,
    reviewCount: null,
    address: "",
    hours: "",
    tags,
    reviewThemes: (discovery.commentThemes ?? []).map((label) => ({ label, count: null })),
    image: discovery.image ?? null,
    source: discovery.sourceUrl ?? null,
    featured: false,
    dataTier: "douyin",
    qualityTier: discovery.image ? "rich" : "indexed",
    reviewSnapshot: false,
    videoClues: [discovery],
    heat: Number(stats.likes) || 0,
    mappable: false,
    cityStyle: false,
    searchText: `${discovery.name} ${discovery.area ?? ""} ${discovery.mood ?? ""} ${tags.join(" ")}`.toLocaleLowerCase(
      "zh-CN"
    )
  };
  return {
    ...place,
    scenarios: scenarioIds(place),
    types: typeIds(place)
  };
}

export function normalizeTeaData({ editorial, places, discoveries = {} }) {
  const mapped = (places.features ?? []).map(normalizeMappablePlace);
  const editorialPlaces = (editorial.shops ?? []).map(normalizeEditorialPlace);
  const mergedNames = new Set(editorialPlaces.map((place) => normalizedName(place.name)));
  const douyinPlaces = (discoveries.discoveries ?? [])
    .filter((discovery) => {
      const name = normalizedName(discovery.name);
      if (!name) return false;
      return ![...mergedNames].some(
        (merged) => merged.includes(name) || name.includes(merged)
      );
    })
    .map(normalizeDiscoveryPlace);
  return {
    metadata: {
      ...places.metadata,
      editorial_shop_count: editorialPlaces.length,
      douyin_count: douyinPlaces.length,
      total_count: mapped.length + editorialPlaces.length + douyinPlaces.length
    },
    places: [...editorialPlaces, ...douyinPlaces, ...mapped]
  };
}

export function toTeaMapCollection(places) {
  const mappable = places.filter((place) => place.mappable);
  return {
    type: "FeatureCollection",
    features: mappable.map((place) => ({
      type: "Feature",
      id: place.mapId,
      geometry: {
        type: "Point",
        coordinates: [place.displayLng, place.displayLat]
      },
      properties: {
        id: place.id,
        mapId: place.mapId,
        name: place.name,
        scenarios: place.scenarios,
        area: place.area,
        category: place.category
      }
    }))
  };
}

export function matchesScenario(place, scenarioId) {
  return scenarioId === "all" || place.scenarios.includes(scenarioId);
}

export function matchesType(place, typeId) {
  return typeId === "all" || (place.types ?? []).includes(typeId);
}

function reviewWeight(place) {
  return Number(place.reviewCount) || Number(place.heat) || 0;
}

function ratingWeight(place) {
  return Number(place.rating) || 0;
}

export function sortPlaces(places, sortId, distanceOf = () => Number.POSITIVE_INFINITY) {
  const sorted = [...places];
  if (sortId === "rating") {
    return sorted.sort(
      (left, right) =>
        ratingWeight(right) - ratingWeight(left) ||
        reviewWeight(right) - reviewWeight(left)
    );
  }
  if (sortId === "nearby") {
    return sorted.sort((left, right) => distanceOf(left) - distanceOf(right));
  }
  return sorted.sort(
    (left, right) =>
      reviewWeight(right) - reviewWeight(left) ||
      ratingWeight(right) - ratingWeight(left)
  );
}
